import { css } from "@emotion/react";
import { useContext, useMemo } from "react";
import { PublicationButton } from "../bits/buttons/PublicationButton";
import { LoadingSpinner } from "../bits/loading";
import Shelf from "@assets/shelf.svg";
import { useUser } from "../hooks/useUser";
import { UserPublicationsContext } from "../contexts/WithPublications/UserPublicationsContext";

export const ArchiveBookshelf = ({
  className,
  homeView,
}: {
  className?: string;
  homeView?: boolean;
}) => {
  const { user } = useUser();
  const { archivedPublications: publications, isLoading } = useContext(
    UserPublicationsContext,
  );

  // Sort publications by title
  const orderedPublications = useMemo(
    () =>
      publications
        ? [...publications].sort((a, b) => {
            const nameA = a.title.toUpperCase();
            const nameB = b.title.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }

            // names must be equal
            return 0;
          })
        : publications,
    [publications],
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!orderedPublications?.length) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 70vh;
        `}
      >
        You have no archived publications
      </div>
    );
  }

  return (
    <>
      <div>
        Archived publications will not show up in your search results, be
        visible in your highlights and notes or available for reading. You can
        unarchive a publication at any time.
      </div>
      <div
        className={className}
        css={css`
          position: relative;
          display: flex;
          overflow: hidden;
          justify-content: center;
          flex-wrap: wrap;
          row-gap: 80px;
          column-gap: 40px;
          padding: 22px 40px 80px 40px;
        `}
      >
        {orderedPublications.map((pub) => {
          const recentPublication = user?.recentPublications.find(
            (recent) => recent.publication === pub.id,
          );
          return (
            <PublicationButton
              homeView={homeView}
              key={pub.filename}
              publication={pub}
              archiveView
              recentPublication={recentPublication}
            />
          );
        })}
        <div
          css={css`
            position: absolute;
            background-repeat: repeat;
            width: 100%;
            height: 100vh;
            z-index: -1;
            margin-top: 20px;
          `}
        >
          {orderedPublications.map((pub) => (
            <div
              key={pub.filename + "shelf"}
              css={({ breakpoints }) => css`
                margin-top: 180px;

                @media (max-width: ${breakpoints.values.xl}px) {
                  margin-top: 120px;
                }
                width: 100%;
                height: 140px;
                background-image: url("${Shelf}");
                z-index: -1;
              `}
            ></div>
          ))}
        </div>
      </div>
    </>
  );
};
